"use client";

import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "next/navigation";
import {
  FilterBar as ZinniaFilterBar,
  type FilterBarChip,
  type FilterBarFilter,
  type FilterBarLabels,
  type FilterBarQuickFilter,
  VisuallyHidden,
} from "@zapier/design-system";
import { useTranslations } from "@zapier/i18n";
import { useNamespacedTranslations } from "./useNamespacedTranslations";
import { useRouterParams } from "../../../hooks/useRouterParams";
import { FilterBarProvider, useFilterBarContext } from "./FilterBarContext";
import type {
  ExclusiveMode,
  FilterDefinition,
  QuickFilterContext,
  QuickFilterDefinition,
  StateChipDefinition,
} from "./types";

export interface FilterBarProps {
  namespace: string;
  filters: FilterDefinition[];
  quickFilters: QuickFilterDefinition[];
  quickFilterContext?: QuickFilterContext;
  /**
   * Chips driven by URL state rather than selectable filters (e.g. Zaps'
   * deactivated / billing-pending quick-filter states). At most ONE renders:
   * the first active entry in array order, so callers order them to express
   * tie-breaks (e.g. billing-pending before deactivated).
   */
  stateChips?: StateChipDefinition[];
  isDisabled?: boolean;
  showFiltersButton?: boolean;
  exclusiveMode?: ExclusiveMode;
  /** Asset wires keyword analytics here. */
  onSearch?: (value: string) => void;
  /** Asset analytics for filter-value changes — receives the filter's `type`. */
  onFilterInteraction?: (type: string) => void;
  searchParamName?: string;
}

export function FilterBar(props: FilterBarProps) {
  return (
    <FilterBarProvider>
      <FilterBarInner {...props} />
    </FilterBarProvider>
  );
}

function FilterBarInner({
  namespace,
  filters,
  quickFilters,
  quickFilterContext,
  stateChips,
  isDisabled,
  showFiltersButton,
  exclusiveMode,
  onSearch,
  onFilterInteraction,
  searchParamName: searchParameterName = "search",
}: FilterBarProps) {
  // Shared chrome (identical across assets). Asset namespace keeps placeholders,
  // hints, clearAll / chips.cleared variants, and filter-specific copy.
  const tShared = useTranslations("FilterBar");
  const t = useNamespacedTranslations(namespace);
  const { registrations, openFilter, openMenu, closeMenu } =
    useFilterBarContext();
  const { search, setParams } = useRouterParams();

  const [searchDraft, setSearchDraft] = useState(search ?? "");

  useEffect(() => setSearchDraft(search ?? ""), [search]);

  const [filterAnnouncement, setFilterAnnouncement] = useState("");

  const searchParameters = useSearchParams();
  const orderByType = useMemo(() => {
    const parameterToType = new Map<string, string>();
    for (const filter of filters) {
      for (const parameter of filter.paramNames) {
        parameterToType.set(parameter, filter.type);
      }
    }
    const order = new Map<string, number>();
    let next = 0;
    for (const [key] of searchParameters?.entries() ?? []) {
      const type = parameterToType.get(key);
      if (type && !order.has(type)) order.set(type, next++);
    }
    for (const filter of filters) {
      if (!order.has(filter.type)) order.set(filter.type, next++);
    }
    return order;
  }, [filters, searchParameters]);

  const orderOf = (key: string, isDraft?: boolean) =>
    isDraft
      ? Number.POSITIVE_INFINITY
      : (orderByType.get(key) ?? Number.MAX_SAFE_INTEGER);

  const chips = registrations
    .map((registration) => registration.chip)
    .filter((chip): chip is NonNullable<typeof chip> => chip !== undefined)
    .map((chip) => ({
      key: chip.key,
      title: chip.title,
      label: chip.label,
      ariaLabel: chip.ariaLabel,
      // Local-link dual-package IconName types are not identical; cast at the DS boundary.
      iconName: chip.iconName as FilterBarChip["iconName"],
      tooltip: chip.tooltip,
      isRemovable: !chip.isDraft,
    })) as FilterBarChip[];

  const isDraftByKey = new Map(
    registrations.map((registration) => [
      registration.type,
      registration.chip?.isDraft,
    ]),
  );
  chips.sort(
    (a, b) =>
      orderOf(a.key, isDraftByKey.get(a.key)) -
      orderOf(b.key, isDraftByKey.get(b.key)),
  );

  const activeStateChip = stateChips?.find((sc) => sc.isActive);
  const activeStateLabel = activeStateChip
    ? t(activeStateChip.labelKey)
    : undefined;
  if (activeStateChip && activeStateLabel !== undefined) {
    chips.push({
      key: activeStateChip.key,
      iconName: activeStateChip.icon as FilterBarChip["iconName"],
      title: activeStateLabel,
      ariaLabel: activeStateLabel,
      isRemovable: true,
    });
  }

  // Built from registrations (not the Zinnia-mapped `chips` above), excluding
  // `isDraft` placeholders — a draft chip appearing while a menu is open isn't
  // real filter state and shouldn't trigger a "chips changed" announcement.
  const chipSignature = [
    ...registrations
      .map((registration) => registration.chip)
      .filter(
        (chip): chip is NonNullable<typeof chip> =>
          chip !== undefined && !chip.isDraft,
      )
      .map(
        (chip) => `${chip.key}:${chip.ariaLabel ?? chip.label ?? chip.title}`,
      ),
    ...(activeStateChip && activeStateLabel
      ? [`${activeStateChip.key}:${activeStateLabel}`]
      : []),
  ].join("|");

  const [lastChipSignature, setLastChipSignature] = useState<
    string | undefined
  >();
  useEffect(() => {
    const previous = lastChipSignature;
    setLastChipSignature(chipSignature);
    if (previous === undefined) return;
    if (previous === chipSignature) return;
    if (chipSignature === "") {
      setFilterAnnouncement(t("chips.cleared"));
      return;
    }
    const summary = chipSignature
      .split("|")
      .map((segment) => segment.replace(/^[^:]+:/, ""))
      .join(", ");
    setFilterAnnouncement(tShared("chips.changed", { summary }));
  }, [chipSignature, lastChipSignature, t, tShared]);

  const zinniaFilters: FilterBarFilter[] = useMemo(
    () =>
      filters.map((filter) => {
        const registration = registrations.find(
          (item) => item.type === filter.type,
        );
        return {
          key: filter.type,
          label: t(filter.labelKey),
          iconName: filter.icon as FilterBarFilter["iconName"],
          // Zinnia FilterBar API: renderMenu receives `{ close }`, not a bare callback.
          // Escape is handled by Zinnia's panel; pass through the controller menu.
          renderMenu: () => registration?.renderMenu(),
          // menuOptions typed on FilterBarFilter in DS 10.17.0+.
          menuOptions: registration?.menuOptions,
        } as FilterBarFilter;
      }),
    [filters, registrations, t],
  );

  const zinniaQuickFilters: FilterBarQuickFilter[] = useMemo(() => {
    const context = quickFilterContext ?? {};
    return quickFilters
      .filter(
        (quickFilter) =>
          !quickFilter.isAvailable || quickFilter.isAvailable(context),
      )
      .map((quickFilter) => {
        const disabled = quickFilter.isDisabled?.(context) ?? false;
        return {
          key: quickFilter.key,
          label: t(quickFilter.labelKey),
          iconName: quickFilter.icon as FilterBarQuickFilter["iconName"],
          isDisabled: disabled,
          trailingIconName: disabled
            ? (quickFilter.trailingIconKey as FilterBarQuickFilter["trailingIconName"])
            : undefined,
          tooltip:
            disabled && quickFilter.tooltipKey
              ? t(quickFilter.tooltipKey)
              : undefined,
          onApply: () => {
            quickFilter.apply(setParams);
            onFilterInteraction?.(quickFilter.key);
          },
        };
      });
  }, [onFilterInteraction, quickFilterContext, quickFilters, setParams, t]);

  const labels: FilterBarLabels = {
    placeholder: exclusiveMode?.inputDisabled
      ? t(exclusiveMode.placeholderKey)
      : t("searchPlaceholder"),
    chips: tShared("chips.aria-label"),
    clearAll: t("clearAll"),
    filterBy: tShared("filterByLabel"),
    filtersButton: tShared("filtersButton"),
    quickFilters: tShared("quickFilters.label"),
    searchForKeyword: tShared("searchForKeyword", {
      keyword: searchDraft.trim(),
    }),
    searchForKeywordHint: t("searchForKeywordHint"),
    resultsAvailable: (count) => tShared("resultsAvailable", { count }),
  };

  const hasActiveFilters =
    registrations.some((registration) => registration.hasValue) ||
    (stateChips?.some((sc) => sc.isActive) ?? false);
  const showClearAll = hasActiveFilters || searchDraft.trim().length > 0;

  const isBillingLocked = Boolean(exclusiveMode?.inputDisabled);

  const handleRemoveChip = (key: string) => {
    closeMenu();
    const definition = filters.find((filter) => filter.type === key);
    if (definition) {
      setParams({ page: 1 }, definition.paramNames);
      return;
    }
    const stateChip = stateChips?.find((sc) => sc.key === key);
    if (stateChip) setParams({ page: 1 }, stateChip.omitParams);
  };

  const handleClearAll = () => {
    setParams({ page: 1 }, [
      ...filters.flatMap((filter) => filter.paramNames),
      ...(stateChips?.flatMap((sc) => sc.omitParams) ?? []),
      searchParameterName,
    ]);
    setSearchDraft("");
    closeMenu();
  };

  const handleKeywordSubmit = (value: string) => {
    const trimmed = value.trim();
    if (trimmed.length > 0) {
      setParams({ [searchParameterName]: trimmed, page: 1 });
    } else {
      setParams({ page: 1 }, [searchParameterName]);
    }
    setSearchDraft(trimmed);
    onSearch?.(trimmed);
    closeMenu();
  };

  return (
    <>
      <VisuallyHidden>
        <div role="status" aria-live="polite" aria-atomic="true">
          {filterAnnouncement}
        </div>
      </VisuallyHidden>
      <ControllerOpenBridge
        filters={filters}
        namespace={namespace}
        onFilterInteraction={onFilterInteraction}
      />
      <ZinniaFilterBar
        chips={chips}
        filters={isBillingLocked ? [] : zinniaFilters}
        inputValue={searchDraft}
        isDisabled={isDisabled}
        isInputDisabled={isBillingLocked}
        labels={labels}
        // Controlled so controller onClose (single-select) actually dismisses
        // the Zinnia panel — not only FilterBarContext.isOpen.
        // `null` = controlled closed; `undefined` would make Zinnia uncontrolled.
        // eslint-disable-next-line unicorn/no-null -- Zinnia FilterBarProps.openFilterKey uses null for closed
        openFilterKey={openFilter ?? null}
        onClearAll={handleClearAll}
        onInputChange={(value) => {
          setSearchDraft(value);

          // Reset to page 1 like onKeywordSubmit — otherwise narrowing the
          // search while on a later page can land on an out-of-range page.
          setParams({ [searchParameterName]: value, page: 1 });
        }}
        onKeywordSubmit={isBillingLocked ? undefined : handleKeywordSubmit}
        onOpenFilterChange={(key) => {
          if (key) openMenu(key);
          else closeMenu();
        }}
        onRemoveChip={handleRemoveChip}
        quickFilters={isBillingLocked ? undefined : zinniaQuickFilters}
        showClearAll={showClearAll}
        showFiltersButton={showFiltersButton && !isBillingLocked}
      />
    </>
  );
}

/**
 * Mounts filter controllers with context-backed `isOpen`. Open-menu UI is
 * controlled on Zinnia FilterBar via `openFilterKey` from this same context.
 */
function ControllerOpenBridge({
  filters,
  namespace,
  onFilterInteraction,
}: {
  filters: FilterDefinition[];
  namespace: string;
  onFilterInteraction?: (type: string) => void;
}) {
  const { openFilter, closeMenu } = useFilterBarContext();
  return (
    <>
      {filters.map((filter) => (
        <filter.Controller
          key={filter.type}
          isOpen={openFilter === filter.type}
          namespace={namespace}
          onClose={closeMenu}
          onInteraction={
            onFilterInteraction
              ? () => onFilterInteraction(filter.type)
              : undefined
          }
        />
      ))}
    </>
  );
}
